import React from 'react';
import styled from 'styled-components';
import { Cell, HeaderCell, HeaderRow, Icon, Row, Table, TableBody, TableHead } from 'components';
import { theme, toRem } from 'design';
import { DEFAULT_POOL_NAME, ITipCalculatorChangeLogEntry } from 'types';
import { buildChangeLogTableRows, IChangeLogTableRow, toTitleCase } from 'utils';

interface IChangeLogTableProps {
  changeLogEntries: ITipCalculatorChangeLogEntry[];
}

const TableWrapper = styled.section`
  width: 100%;
`;

const Item = styled.div`
  display: flex;
  flex-flow: row nowrap;
  font-weight: ${theme.font.weight.semiBold};
`;

const ItemSub = styled.div<{ marginLeft?: number }>`
  font-weight: ${theme.font.weight.regular};
  color: ${theme.color.neutral[600]};
  margin-left: ${({ marginLeft }) => toRem(marginLeft) || 0};
`;

const Details = styled.div`
  display: flex;
  flex-flow: row nowrap;

  svg {
    margin: 0 ${toRem(4)};
  }
`;

const ChangeLogTable: React.FC<IChangeLogTableProps> = ({ changeLogEntries }) => {
  const rows = buildChangeLogTableRows(changeLogEntries);

  const formatItem = (entry: IChangeLogTableRow) => {
    // If the segment is the default pool, we don't need to show the segment or job
    if (entry.item.segment === DEFAULT_POOL_NAME) {
      return <Item>{DEFAULT_POOL_NAME}</Item>;
    }

    return (
      <Item>
        <>
          {entry.item?.name}
          {entry.item.segment && ', '}
        </>
        {entry.item.segment && <ItemSub marginLeft={4}>{entry.item.segment}</ItemSub>}
        {entry.item.job && <ItemSub>, {toTitleCase(entry.item.job)}</ItemSub>}
      </Item>
    );
  };

  const formatDetails = (entry: IChangeLogTableRow) => {
    if (!entry.details) return <Details>--</Details>;
    return (
      <Details>
        {entry.details.from} <Icon icon="ArrowRight" />
        {entry.details.to}
      </Details>
    );
  };

  return (
    <TableWrapper data-cy="table">
      <Table scrollable data-cy="change-log-table">
        <TableHead>
          <HeaderRow>
            <HeaderCell>Number</HeaderCell>
            <HeaderCell>Category</HeaderCell>
            <HeaderCell>Event</HeaderCell>
            <HeaderCell>Item</HeaderCell>
            <HeaderCell>Details</HeaderCell>
          </HeaderRow>
        </TableHead>
        <TableBody>
          {rows.map((entry: IChangeLogTableRow, idx: number) => (
            <Row key={`entry-${idx}`}>
              <Cell wrapText data-label="Number" fontSize="small">
                {entry.number}
              </Cell>
              <Cell wrapText data-label="Category" fontWeight="semiBold" fontSize="small">
                {entry.category}
              </Cell>
              <Cell data-label="Event" color={theme.color.neutral[600]} fontSize="small">
                {entry.event}
              </Cell>
              <Cell data-label="Item" fontSize="small">
                {formatItem(entry)}
              </Cell>
              <Cell data-label="Details" color={theme.color.neutral[600]} fontSize="small">
                {formatDetails(entry)}
              </Cell>
            </Row>
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  );
};
export default ChangeLogTable;
